import isEqual from "lodash/isEqual";
import React from "react";

import * as paramsActions from "core/actions/paramsActions";
import { Intent, IntentProvider } from "core/components/IntentContext";
import initialState from "core/reducers/initialState";
import Class from "core/util/Class";
import { makeCancelable } from "core/util/PromiseUtil";
import ServiceRegistry from "core/util/ServiceRegistry";
import UrlUtil from "core/util/UrlUtil";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { ErrorSource, IError, ILoggerService } from "../service/logger/ILoggerService";
import { currentMode } from "../util/Mode";
import { ErrorDisplay } from "../components/ErrorDisplay";

// tslint:disable:max-classes-per-file only-arrow-functions

/**
 * query component that infers data type!
 *
 * use like:
 *
 * <QueryComponent type="many" entity={User} args={{ sort: "-firstName" }}>
 * 		{
 * 			({data}) => data.map(this.renderUser)   // data will be of type User[]
 * 		}
 * </QueryComponent>
 *
 * @author chris
 */

export type QueryType = "one" | "many" | "count";

interface IQueryState<E, T extends QueryType> {
	readonly data: null | (
		T extends "one" ? E
			: T extends "many" ? E[]
			: T extends "count" ? { count: number; } :
			any
	);
	readonly error?: IError;
	readonly loading: boolean;
}

type Params<P> = (pageParams: any, user: any) => P;

interface IQueryArgs<E, T extends QueryType, P = any> extends IQueryState<E, T> {
	params: P;
	forceRefresh: () => void;
}

interface IQueryProps<T extends QueryType, E = any, P = any> {
	args?: P | Params<P>;
	children: (args: IQueryArgs<E, T, P>) => React.ReactNode;
	entity: Class<E>;
	variables?: object;
}

interface IQueryOwnProps<E, P, T extends QueryType> extends IQueryProps<T, E, P> {
	root?: boolean; // if true, will use IQueryService.load
	type: T;
}

interface IQueryStateProps<P> {
	params: P;
	refresh: any;
}

interface IQueryDispatchProps {
	actions: typeof paramsActions;
}

type ResultProps<E, P, T extends QueryType> = IQueryStateProps<P> & IQueryDispatchProps & IQueryOwnProps<E, P, T>;

class QueryComponent<E, P, T extends QueryType> extends React.Component<ResultProps<E, P, T>, IQueryState<E, T>> {

	public state = { data: null, error: undefined, loading: false };

	private promise?: ReturnType<typeof makeCancelable>;

	private renderErrors = (error: IError) => (
		<ErrorDisplay error={error} location="panel" type={ErrorSource.API} />
	);

	public render() {
		const { error } = this.state;
		const children = this.props.children({
			...this.state,
			params: this.props.params,
			forceRefresh: this.props.actions.forceQueryRefresh
		});

		// in dev mode if there are errors show the errors and the children, otherwise in prod only show the errors
		return (
			<IntentProvider intent={this.state.loading && Intent.LOADING || ""}>
				{(error && currentMode.isDev() && (
					<React.Fragment>
						{this.renderErrors(error)}
						{children}
					</React.Fragment>
				)) || (error && (
					this.renderErrors(error)
				)) || (
					children
				)}
			</IntentProvider>
		);
	}

	public componentDidMount() {
		this.loadData();
	}

	public componentDidUpdate(prevProps: any) {
		if (!isEqual(prevProps.params, this.props.params) || prevProps.refresh !== this.props.refresh) {
			this.loadData();
		}
	}

	public componentWillUnmount() {
		if (this.promise) {
			this.promise.cancel();
		}
	}

	private onError(error: IError) {
		// allow the service to log the error remotely if configured
		const errorCode = this.state.error || ServiceRegistry.get(ILoggerService).reportError(ErrorSource.API, error, "api", this.state.error);

		this.setState({error: {...error, errorCode}, loading: false});

		if(currentMode.isDev()) {
			throw error;
		}
	}

	private loadData = () => {
		const { entity, params, root, type, variables } = this.props;

		if (root) {
			const queryPromise = ServiceRegistry.getQueryService().load(entity, params, variables);

			this.promise = makeCancelable(queryPromise);

			this.setState({loading: true}, () => {
				this.promise.promise.then((res: any) => {

					const error = { message: "API Error", info: JSON.stringify(res.errors) };

					if(res.errors) {
						this.onError(error);
					} else {
						this.setState({
							data: res.data || null,
							error: null,
							loading: false
						});
					}

				}).catch((error) => {
					// Check if error is an object and has a message field
					if (error && typeof error === 'object' && error.message && error.message.includes("Store reset while query was in flight")) {
						console.log(error.message)
					} else {
						this.onError(error);
					}
				});
			});

		} else {
			const queryPromise = ServiceRegistry.getQueryService().find(type as any, entity, params as any, variables);

			this.promise = makeCancelable(queryPromise);

			this.setState({loading: true}, () => {
				this.promise.promise.then((res: any) => {

					const error = res.errors && { message: "API Error", info: JSON.stringify(res.errors) };

					if(res.errors) {
						this.onError(error);
					} else {
						this.setState({
							data: res.data.entities || res.data.entity || null,
							error: null,
							loading: false
						});
					}

				}).catch((error) => {
					// Check if error is an object and has a message field
					if (error && typeof error === 'object' && error.message && error.message.includes("Store reset while query was in flight")) {
						console.log(error.message)
					} else {
						this.onError(error);
					}
				});
			});
		}
	}
}

function mapStateToProps<E, P, T extends QueryType>(state: any, ownProps: IQueryOwnProps<E, P, T>): IQueryStateProps<P> {
	const { args } = ownProps;

	const pageParams = UrlUtil.getSearchParams();

	const params = typeof args === 'function' ? (args as any)(pageParams, state.user) : args || {};

	return { params, refresh: state.params.refresh };
}

function mapDispatchToProps(dispatch: Dispatch<typeof initialState>): IQueryDispatchProps {

	return {
		actions: bindActionCreators(paramsActions, dispatch)
	};
}

const ConnectedQuery = connect(mapStateToProps, mapDispatchToProps)(QueryComponent);

function Query<E, P, T extends QueryType>(props: IQueryOwnProps<E, P, T>): JSX.Element {
	return <ConnectedQuery {...props} />;
}

Query.One = function<E, P>(props: IQueryProps<"one", E, P>) {
	return <ConnectedQuery {...props} type="one" />;
};

Query.Many = function<E, P>(props: IQueryProps<"many", E, P>) {
	return <ConnectedQuery {...props} type="many" />;
};

Query.Count = function<E, P>(props: IQueryProps<"count", E, P>) {
	return <ConnectedQuery {...props} type="count" />;
};

Query.Root = function<E, P>(props: IQueryProps<"one", E, P>) {
	return <ConnectedQuery {...props} type="one" root={true} />;
};

export default Query;



// WEBPACK FOOTER //
// ./src/core/api/QueryComponent.tsx